// "Pending actions": which decision lists the person holds, and what each one's card says
// while its count is on its way and once it has arrived. No components in here: only each
// list's name, icon, count, the few words beside the count, the longer line behind it and
// the address. Every card held has its fixed place from the first render (DECISION_ORDER,
// the owner's mockup): named at once with a grey bar where its number will be, and never
// moved when a count lands.
//
// A card (components/cards/CompactCard) is the list's icon and short name, its count and
// one to three grey words beside it ("412 waiting"). The longer line (the hint: how long
// the oldest has waited, who is counted, where the rest are) and the time a kept count
// was read are the card's hover title, with a sentence saying what the number counts.
// The count and the line are a promise about the list the card opens, so each is written
// against what that list shows when it is opened from here:
//   • Red Flags Appeals opens on every appeal, decided ones too. The count is of that
//     list's own undecided rows, so its card opens the list narrowed to them (its
//     Status filter on Pending and Under review, `status=open`): the N on the card are
//     the N in the list.
//   • Yellow Flag Appeals is counted the same way, but opens narrowed to flags dated
//     THIS month, which would hide an older appeal that is still waiting. So its card
//     opens the list on the days the waiting appeals were raised (the list's own
//     "Raised" window), narrowed to the undecided ones in the same way.
//   • Approvals (22 Oct 2026, the product owner: one card for every kind of approval)
//     opens My Approvals on what is waiting (`status=pending&dates=all`): every leave
//     request, attendance correction and comp-off claim of this person's people that
//     nobody has decided, as far back as the page reads (60 days; comp-off claims 90),
//     and the page says so when opened this way. The server keeps that count in the
//     background, so it can be some minutes behind the page: the card's hover always says
//     when its count was read. (Probation confirmations, decided under My Approvals too,
//     keep their own card.)
//   • Start Exit opens on its "Pending review" tab: the people counted. The list is
//     worked out live; the count is kept by the server and can be a couple of minutes
//     old, so past half a minute the card's hover says when it was taken.
//   • PIP opens on the plans that need a decision, from every quarter.
//   • Probation Confirmation opens on the person's own pending list. That list is
//     theirs across every project, and the hint says so.
//   • The Onboarding dashboard opens on its list of joiners with no buddy, and the hint
//     says in a few words why they matter.
//   • Stores › Store Requests opens on the pending requests.
//   • Social Moderation opens on the reported posts still open from the last 30 days;
//     the rest of the count is one tab along, on AI review, and the hint says so.
//   • Hiring (22 Oct 2026) opens on the Hiring dashboard's Notice period tab narrowed to
//     the people serving notice whom no hiring replaces yet: the rows it counts. Offered
//     only to someone who can act on such a row there (raise, ask for or link hiring), and
//     not to someone with no 1Office account to read with: the tab lists nobody for them.
//     The count is the tab's own read (in the person's own 1Office sign-in, so it cannot be
//     kept in the background), given a short wait; past it the card says "Open".
//   • Approvals, for a manager whose people reach beyond the project the server read, is
//     "not counted here" (a number would be short of the page): a grey "Open" whose hover
//     says why, not asked again.
import {
  CalendarClock, ClipboardCheck, Gavel, LogOut, MessageSquare, Scale, Store, TrendingUp, UserCheck, UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type { MenuItem } from '../../stores/menuStore';
import { dashboardListPath, type DashTab } from '../../navigation/dashboards';
import { HIRING_LIST } from '../../components/dashboard/lists/hiring';
import { NO_BUDDY_SLUG } from '../../components/dashboard/lists/onboarding';
import { DRILL_ROUTES, DRILL_STATUS_OPEN, buildDrillUrl } from '../../utils/drilldown';
import { heldPage } from './homeMenu';
import {
  fmtNumber, postsWaitingLine, postsWaitingTotal, waitedLine,
  type ApprovalsNotCounted, type ApprovalsPending, type ExitReviews, type NoticeNotRaised, type OpenAppeals,
  type PostsWaiting, type ProbationWaiting,
} from './homeNumbers';
import type { Read } from './useRead';

// My Approvals: leave, attendance-correction and comp-off requests, decided here.
const APPROVALS_PATH = '/approvals';
const START_EXIT_PATH = '/governance/start-exit';
const PIP_PATH = '/reports/pip';
const PROBATION_PATH = '/approvals/probation-confirmation';
const STORES_PATH = '/master-management/stores';
const SOCIAL_MODERATION_PATH = '/governance/social-moderation';
// A working list of new joiners. It is a dashboard, so it is held as one, not as a page.
const ONBOARDING: DashTab = 'Onboarding';
// The people serving notice at the person's stores: a tab of the Hiring dashboard.
const HIRING: DashTab = 'Hiring';

/**
 * Where the Approvals card opens: My Approvals on every request still waiting, with no
 * Requested-date limit of the page's own. That is not every request ever raised: the
 * server reads the last 60 days (90 for comp-off claims), and the page says so when
 * opened this way.
 */
export const APPROVALS_WAITING_PATH = `${APPROVALS_PATH}?status=pending&dates=all`;

/** The lists that have a count, as menu rows; null where the person does not hold the page. */
export interface HeldDecisionLists {
  redAppeals: MenuItem | null;
  yellowAppeals: MenuItem | null;
  /** My Approvals (leave, attendance corrections, comp-off claims). */
  approvals: MenuItem | null;
  startExit: MenuItem | null;
  pip: MenuItem | null;
  probation: MenuItem | null;
  /** The Onboarding dashboard is among the person's dashboards. */
  onboarding: boolean;
  stores: MenuItem | null;
  socialModeration: MenuItem | null;
  /**
   * The Hiring dashboard is among the person's dashboards (its tick, and the server says
   * hiring is on for them in this project) and they can act on a row of its Notice period
   * tab there: link an opening, or raise or ask for hiring (the server's word that hiring
   * requests are theirs here, which it gives only where Raise is on for the project).
   * `'asking'` while the server has not said yet, for someone who holds its menu row: the
   * card holds its place meanwhile (nothing is counted until it has said yes).
   */
  noticeHiring: boolean | 'asking';
}

/** What the server's hiring access says this person may do, for the Hiring card. */
export interface HiringActs {
  canManage: boolean;
  canSeeRequests: boolean;
  /** The person holds the Hiring dashboard's menu row and the server has not answered yet. */
  asking?: boolean;
}

const NO_HIRING_ACTS: HiringActs = { canManage: false, canSeeRequests: false };

/** Who is looking, as far as a list's own page decides it. */
export interface DecisionViewer {
  /**
   * My Approvals opens for them: it shows "access needed" to a sign-in with no 1Office user
   * id (an external user, userId 0), so no Approvals card is offered to one.
   */
  opensMyApprovals: boolean;
}

const ANY_VIEWER: DecisionViewer = { opensMyApprovals: true };

export function heldDecisionLists(
  pages: MenuItem[],
  tabs: readonly DashTab[],
  hiring: HiringActs = NO_HIRING_ACTS,
  viewer: DecisionViewer = ANY_VIEWER,
): HeldDecisionLists {
  let noticeHiring: boolean | 'asking' = tabs.includes(HIRING) && (hiring.canManage || hiring.canSeeRequests);
  if (hiring.asking) noticeHiring = 'asking';
  return {
    redAppeals: heldPage(pages, DRILL_ROUTES.appeals),
    yellowAppeals: heldPage(pages, DRILL_ROUTES.yellowAppeals),
    approvals: viewer.opensMyApprovals ? heldPage(pages, APPROVALS_PATH) : null,
    startExit: heldPage(pages, START_EXIT_PATH),
    pip: heldPage(pages, PIP_PATH),
    probation: heldPage(pages, PROBATION_PATH),
    onboarding: tabs.includes(ONBOARDING),
    stores: heldPage(pages, STORES_PATH),
    socialModeration: heldPage(pages, SOCIAL_MODERATION_PATH),
    noticeHiring,
  };
}

export interface DecisionCounts {
  redAppeals: Read<OpenAppeals>;
  yellowAppeals: Read<OpenAppeals>;
  /** What waits on My Approvals, or that it is not counted here. Kept by the server. */
  approvals: Read<ApprovalsPending>;
  exitReviews: Read<ExitReviews>;
  plansToDecide: Read<number>;
  probation: Read<ProbationWaiting>;
  noBuddy: Read<number>;
  storeRequests: Read<number>;
  posts: Read<PostsWaiting>;
  /** People serving notice whose hiring is not raised yet, or that the tab lists nobody this person can act on. */
  noticeHiring: Read<NoticeNotRaised>;
}

export type DecisionId =
  | 'red-appeals' | 'yellow-appeals' | 'approvals' | 'exit-reviews' | 'improvement-plans'
  | 'probation' | 'no-buddy' | 'notice-hiring' | 'store-requests' | 'posts';

/**
 * What each list is called on the home screen, whatever its card says: with its count,
 * with no number, or a grey 0. Short, as the owner's mockup of 22 Oct 2026 named them
 * ("New joiners", "Probation"): the words beside the count finish the sentence
 * ("5 without a buddy", "3 to confirm").
 */
export const DECISION_LABEL: Record<DecisionId, string> = {
  'red-appeals': 'Red-flag appeals',
  'yellow-appeals': 'Yellow-flag appeals',
  approvals: 'Approvals',
  'exit-reviews': 'Exit reviews',
  'improvement-plans': 'Improvement plans',
  probation: 'Probation',
  'no-buddy': 'New joiners',
  // The owner's name for it (22 Oct 2026). The Dashboards tab's Hiring card is the whole dashboard.
  'notice-hiring': 'Hiring',
  'store-requests': 'New store requests',
  posts: 'Posts to review',
};

/** The one to three grey words beside each list's count, the same at 0, 1 or many: "412 waiting". */
export const DECISION_WORDS: Record<DecisionId, string> = {
  'red-appeals': 'open',
  'yellow-appeals': 'open',
  approvals: 'waiting',
  'exit-reviews': 'to decide',
  'improvement-plans': 'to decide',
  probation: 'to confirm',
  'no-buddy': 'without a buddy',
  'notice-hiring': 'on notice, not raised',
  'store-requests': 'to approve',
  posts: 'waiting',
};

/**
 * What each list's number counts, in full: the first line of the card's hover title.
 * The hint and the time a kept count was read follow it there.
 */
export const DECISION_ABOUT: Record<DecisionId, string> = {
  'red-appeals': 'Red-flag appeals still to decide (pending or under review)',
  'yellow-appeals': 'Yellow-flag appeals still to decide (pending or under review)',
  approvals: 'Leave requests, attendance corrections and comp-off claims waiting on My Approvals',
  'exit-reviews': 'Exit reviews still to decide',
  'improvement-plans': 'Improvement plans still to decide',
  probation: 'Probation confirmations waiting for you',
  'no-buddy': 'New joiners waiting for a buddy',
  'notice-hiring': 'People serving notice with no hiring raised or linked yet',
  'store-requests': 'New store requests waiting for approval',
  posts: 'Posts waiting for review',
};

/** The icon in the square at the top of each list's card. */
export const DECISION_ICON: Record<DecisionId, LucideIcon> = {
  'red-appeals': Gavel,
  'yellow-appeals': Scale,
  approvals: ClipboardCheck,
  'exit-reviews': LogOut,
  'improvement-plans': TrendingUp,
  probation: UserCheck,
  'no-buddy': UserPlus,
  // The icon of the Notice period tab it opens.
  'notice-hiring': CalendarClock,
  'store-requests': Store,
  posts: MessageSquare,
};

/** What every list's card has, whatever it says. */
interface CardBase {
  id: DecisionId;
  /** The list's short name: the card's title. */
  label: string;
  /** Where the whole card goes: the list, opened on what its count counts. */
  to: string;
}

/**
 * One list's card, in its fixed place (DECISION_ORDER):
 * - `loading`: its count is on its way. The card is there already, named, with a grey bar
 *   where the number will be, so nothing moves when the number lands.
 * - `waiting`: something waits. An amber count.
 * - `clear`: nothing waits. A light grey 0, opening the list where it would have.
 * - `not-known`: the count failed, or the answer had no number. A grey "Open"; `why` is the
 *   server's reason when it gave one, for the hover.
 */
export type DecisionCard =
  | (CardBase & { state: 'loading' })
  | (CardBase & {
    state: 'waiting';
    count: number;
    /**
     * The longer line behind the count, in the card's hover title: how long the oldest has
     * waited, who is counted, where the rest are. Never the time the count was read: that
     * is `asOf` (see decisionHover).
     */
    hint: string | null;
    /** When the count was read ("10:40 am"), for a count the server keeps; null for one read just now. */
    asOf: string | null;
  })
  | (CardBase & {
    state: 'clear';
    /** When that zero was read, for a count the server keeps in the background. */
    asOf: string | null;
  })
  | (CardBase & { state: 'not-known'; why: string | null });

export interface DecisionList {
  /** The cards, in DECISION_ORDER. A list the person does not hold, or whose count was refused where that means "not yours", has none. */
  cards: DecisionCard[];
}

/**
 * The cards' one order, from the first render to the last: the owner's mockup of 22 Oct
 * 2026 (Approvals, Red-flag appeals, Exit reviews, Hiring, Improvement plans, New joiners,
 * Probation, Yellow-flag appeals), then the two lists the mockup did not show. A card never
 * moves when a count lands: a grey 0 keeps its place.
 */
export const DECISION_ORDER: readonly DecisionId[] = [
  'approvals', 'red-appeals', 'exit-reviews', 'notice-hiring', 'improvement-plans', 'no-buddy', 'probation',
  'yellow-appeals', 'store-requests', 'posts',
];

/** Why the server does not count My Approvals for this person: the second line of the card's hover. */
export const APPROVALS_NOT_COUNTED: Record<ApprovalsNotCounted, string> = {
  'outside-project': 'Not counted here: some of your team work outside this project',
};

/**
 * A card's hover title, one line each: what its number counts in full, then its hint (if
 * it has one), then when a kept count was read ("As of 9:40 am"). Nothing of it is
 * printed on the card, which says only the count and its few words.
 */
export function decisionHover(id: DecisionId, hint: string | null, asOf: string | null): string {
  return [DECISION_ABOUT[id], hint, asOf ? `As of ${asOf}` : null].filter(Boolean).join('\n');
}

/** The hover of a card with no number: what it would count, the server's reason if it gave one, and where to look. */
export function notKnownHover(id: DecisionId, why: string | null): string {
  return [DECISION_ABOUT[id], why, 'Open the list to see what is waiting'].filter(Boolean).join('\n');
}

/** How many lists have something waiting, for the tab's "(N)"; null while a count is still on its way. */
export function waitingListCount(list: DecisionList): number | null {
  if (list.cards.some((c) => c.state === 'loading')) return null;
  return list.cards.filter((c) => c.state === 'waiting').length;
}

/** True when "Pending actions" has a card (loading or not) to draw. */
export function hasDecisionCards(list: DecisionList): boolean {
  return list.cards.length > 0;
}

/** What a count says once its answer is in: a number (0 included), no number (with the server's reason, if any), or no card at all. */
type Counted = { count: number; hint: string | null; asOf: string | null; to: string };
type Settled = Counted | { unknown: string | null } | 'absent';
const UNKNOWN: Settled = { unknown: null };

/**
 * The cards, in DECISION_ORDER. A list the person does not hold is simply absent. So is one
 * whose count was refused or failed and that is not offered without a count (the four
 * counted by their own page's calls), and the Hiring list when its tab lists nobody this
 * person can act on.
 * `today` is India's date, the end of the window the yellow list is opened on.
 */
export function buildDecisionList(held: HeldDecisionLists, counts: DecisionCounts, today: string): DecisionList {
  const cards = new Map<DecisionId, DecisionCard>();
  // `to` is the list's own address: where the card goes while its count is on its way, when
  // its count is not known, and (unless the count says otherwise) with any count.
  //
  // `offered` is set for the six lists counted by a call of their own (approvals, exit
  // reviews, improvement plans, probation, joiners, people on notice with no hiring). That
  // call can fail, or not know its number yet, on a day the list itself still opens; and
  // before they had a count most of them were offered by name. So that is what a count that
  // is not known falls back to: a card with no number. The other four are counted by their
  // own page's calls, so the page would meet the same failure; and a refused store-requests
  // count is the Stores page showing no such tab, which a card must not then offer.
  //
  // `zeroAsOf` is when a zero was read, for the one count here the server keeps in the
  // background (approvals): a zero is a number too, and it can be some minutes old.
  const settle = <T,>(
    read: Read<T>,
    id: DecisionId,
    to: string,
    describe: (value: T) => Settled,
    { offered = false, zeroAsOf = () => null }: { offered?: boolean; zeroAsOf?: (value: T) => string | null } = {},
  ) => {
    const label = DECISION_LABEL[id];
    if (read.status === 'off') return;
    if (read.status === 'loading') { cards.set(id, { id, label, to, state: 'loading' }); return; }
    // A failed call and an answer without the number are one case. Neither is a zero.
    const made: Settled = read.status === 'ready' ? describe(read.value) : UNKNOWN;
    if (made === 'absent') return;
    if ('unknown' in made) {
      if (offered) cards.set(id, { id, label, to, state: 'not-known', why: made.unknown });
    } else if (made.count === 0) {
      cards.set(id, { id, label, to: made.to, state: 'clear', asOf: read.status === 'ready' ? zeroAsOf(read.value) : null });
    } else {
      cards.set(id, { id, label, to: made.to, state: 'waiting', count: made.count, hint: made.hint, asOf: made.asOf });
    }
  };

  if (held.approvals) {
    const to = APPROVALS_WAITING_PATH;
    settle(counts.approvals, 'approvals', to, (waiting) => {
      if ('notCounted' in waiting) return { unknown: APPROVALS_NOT_COUNTED[waiting.notCounted] };
      return {
        count: waiting.count,
        // The home screen's one wording for a wait (homeNumbers: waitedLine).
        hint: waitedLine({ count: waiting.count, oldestDays: waiting.oldestWaitDays, oldestRaisedOn: null }),
        asOf: waiting.asOf,
        to,
      };
    }, { offered: true, zeroAsOf: (waiting) => ('notCounted' in waiting ? null : waiting.asOf) });
  }

  if (held.redAppeals) {
    const to = buildDrillUrl(DRILL_ROUTES.appeals, { status: DRILL_STATUS_OPEN });
    settle(counts.redAppeals, 'red-appeals', to, (red) => ({ count: red.count, hint: waitedLine(red), asOf: null, to }));
  }

  if (held.startExit) {
    const to = `${START_EXIT_PATH}?tab=pending`;
    settle(counts.exitReviews, 'exit-reviews', to, (exits) => ({
      count: exits.count,
      hint: 'People to excuse or exit',
      asOf: exits.asOf,
      to,
    }), { offered: true });
  }

  const hiringTo = dashboardListPath(HIRING, HIRING_LIST.noticeNotRaised);
  if (held.noticeHiring === 'asking') {
    // The server has not said yet whether this person may act on the tab: the card holds its
    // place while it is asked, and leaves if the answer is no.
    cards.set('notice-hiring', { id: 'notice-hiring', label: DECISION_LABEL['notice-hiring'], to: hiringTo, state: 'loading' });
  } else if (held.noticeHiring) {
    settle(counts.noticeHiring, 'notice-hiring', hiringTo, (people) => (people === 'cannot-act' ? 'absent' : {
      count: people,
      // The words beside the count say it all ("9 on notice, not raised"); the hover says it in full.
      hint: null,
      asOf: null,
      to: hiringTo,
    }), { offered: true });
  }

  if (held.pip) {
    const to = `${PIP_PATH}?tab=decision`;
    settle(counts.plansToDecide, 'improvement-plans', to, (plans) =>
      ({ count: plans, hint: 'Ended without improvement', asOf: null, to }), { offered: true });
  }

  if (held.onboarding) {
    const to = dashboardListPath(ONBOARDING, NO_BUDDY_SLUG);
    settle(counts.noBuddy, 'no-buddy', to, (joiners) => ({
      count: joiners,
      // Why it matters, in a few words. The dashboard it opens names the filter in its
      // own banner ("waiting for a buddy").
      hint: 'Cannot start until a buddy is assigned',
      asOf: null,
      to,
    }), { offered: true });
  }

  if (held.probation) {
    const to = `${PROBATION_PATH}?view=pending`;
    settle(counts.probation, 'probation', to, ({ waiting, overdue }) => ({
      count: waiting,
      // Not the selected project's: the person's own queue, wherever the employee works.
      hint: overdue ? `${fmtNumber(overdue)} overdue` : 'Across all your projects',
      asOf: null,
      to,
    }), { offered: true });
  }

  if (held.yellowAppeals) {
    const plain = buildDrillUrl(DRILL_ROUTES.yellowAppeals, { status: DRILL_STATUS_OPEN });
    settle(counts.yellowAppeals, 'yellow-appeals', plain, (yellow) => ({
      count: yellow.count,
      hint: waitedLine(yellow),
      asOf: null,
      to: yellow.oldestRaisedOn
        ? buildDrillUrl(DRILL_ROUTES.yellowAppeals, { startDate: yellow.oldestRaisedOn, endDate: today, status: DRILL_STATUS_OPEN })
        : plain,
    }));
  }

  if (held.stores) {
    const to = `${STORES_PATH}?tab=requests`;
    settle(counts.storeRequests, 'store-requests', to, (pending) => ({ count: pending, hint: 'Sent from the mobile app', asOf: null, to }));
  }

  if (held.socialModeration) {
    settle(counts.posts, 'posts', SOCIAL_MODERATION_PATH, (posts) => {
      const total = postsWaitingTotal(posts);
      if (total === null) return UNKNOWN;
      return { count: total, hint: postsWaitingLine(posts), asOf: null, to: SOCIAL_MODERATION_PATH };
    });
  }

  return { cards: DECISION_ORDER.flatMap((id) => cards.get(id) ?? []) };
}
